import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import * as messages from 'common/constants/messages/validations';
import { userSchemaBase } from 'common/constants/userSchemaBase';
import { useStore } from 'common/store';
import { Firm } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { CustomTextField } from 'components';
import { useFormValidation } from 'services/hooks/useFormValidation';
import { FormValueUserCreate, UserCreateFormProps } from './UserCreateForm.props';
import { classes, StyledGrid } from './UserCreateForm.styles';

const constraints = {
  ...userSchemaBase,
  password: {
    presence: { allowEmpty: false, message: messages.IS_REQUIRED },
    length: {
      maximum: 128,
    },
    format: {
      pattern: /^(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_]).{9,}$/,
      message: `^${messages.PASSWORD_ERROR_MESSAGE}`,
    },
  },
  re_password: {
    presence: { allowEmpty: false, message: messages.IS_REQUIRED },
    length: {
      maximum: 128,
    },
    equality: 'password',
  },
  firm: {
    presence: { allowEmpty: false, message: messages.IS_REQUIRED },
  },
};

const UserCreateForm = ({ defaultFormState: { values, errors, dbErrors }, onFormChange }: UserCreateFormProps) => {
  const { formValidated, validateForm } = useFormValidation(constraints);
  const [formValues, setFormValues] = useState<FormValueUserCreate>(values);

  const [{ userManagementFirmList, firmInfo, user }] = useStore() as unknown as UseStoreValues;

  useEffect(() => {
    validateForm(formValues);
  }, [validateForm, formValues]);

  useEffect(() => {
    onFormChange(formValidated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValidated]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (user && !user.is_superuser) {
      setFormValues(prev => ({
        ...prev,
        firm: firmInfo?.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filterFirmOptions = createFilterOptions<Firm>({
    stringify: option => option.name as string,
  });

  const handleFirmChange = useCallback((__event: React.ChangeEvent<object>, newValue: Firm | null) => {
    if (newValue) {
      setFormValues(prev => ({
        ...prev,
        firm: newValue?.id,
      }));
    }
  }, []);

  return (
    <StyledGrid container>
      <CustomTextField
        required
        fullWidth
        id="user-name"
        name="first_name"
        type="text"
        label="First name"
        className={classes.margin}
        value={formValues.first_name as string}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <CustomTextField
        required
        fullWidth
        id="last-name"
        name="last_name"
        type="text"
        label="Last name"
        className={classes.margin}
        value={formValues.last_name as string}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <CustomTextField
        required
        fullWidth
        id="user-email"
        name="email"
        type="text"
        label="Email address"
        className={classes.margin}
        value={formValues.email as string}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <CustomTextField
        required
        fullWidth
        id="user-password"
        name="password"
        type="password"
        label="Password"
        className={classes.margin}
        value={formValues.password as string}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      <CustomTextField
        required
        fullWidth
        id="user-confirm-password"
        name="re_password"
        type="password"
        label="Confirm password"
        className={classes.margin}
        value={formValues.re_password as string}
        onChange={handleChange}
        formErrors={errors}
        dbErrors={dbErrors}
      />
      {user?.is_superuser && userManagementFirmList && (
        <Autocomplete
          id="firms-autocomplete-user-create-form"
          options={userManagementFirmList}
          filterOptions={filterFirmOptions}
          getOptionLabel={(option: Firm) => option.name as string}
          className={classes.textField}
          onChange={handleFirmChange}
          fullWidth
          value={
            typeof formValues.firm === 'object'
              ? formValues.firm
              : userManagementFirmList.find(firm => firm.id === formValues.firm) || null
          }
          renderInput={params => (
            <CustomTextField
              required
              name="firm"
              label="Select a Firm"
              type="text"
              {...params}
              formErrors={errors}
              dbErrors={dbErrors}
            />
          )}
        />
      )}
    </StyledGrid>
  );
};

export default UserCreateForm;
